import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost, apiUpload, fetchAllPaginated } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import SearchableSelect from '../components/SearchableSelect';

// =====================================================================
// CONSTANTES DO WIZARD
// =====================================================================
const STORAGE_KEY = 'gabaritoWizardState_v1';

const STEPS = [
  { id: 'exam', label: 'Prova', hint: 'Selecionar ou criar' },
  { id: 'classes', label: 'Turmas', hint: 'Quem vai fazer' },
  { id: 'answers', label: 'Respostas', hint: 'Gabarito oficial' },
  { id: 'template', label: 'Template', hint: 'Folha de resposta' },
  { id: 'review', label: 'Revisão', hint: 'Confirmar tudo' },
];

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

// =====================================================================
// HELPERS
// =====================================================================
const loadWizardState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveWizardState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* silencioso */
  }
};

const clearWizardState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* silencioso */
  }
};

// =====================================================================
// SELECT DE DESCRITOR COM BUSCA (reaproveitado da versão anterior)
// =====================================================================
function SearchableDescriptorSelect({ value, onChange, descriptors, darkMode, questionNum }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const filteredDescriptors = useMemo(() => {
    if (!searchTerm.trim()) return descriptors;
    const term = searchTerm.toLowerCase();
    return descriptors.filter(desc =>
      desc.descriptor_code?.toLowerCase().includes(term) ||
      desc.descriptor_name?.toLowerCase().includes(term) ||
      desc.description?.toLowerCase().includes(term)
    );
  }, [descriptors, searchTerm]);

  const selectedDescriptor = useMemo(
    () => descriptors.find(d => d.id === value),
    [descriptors, value]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-2 py-1.5 rounded-md text-xs text-left flex items-center justify-between ${darkMode
          ? 'bg-slate-700/50 border-slate-600 text-slate-200 hover:bg-slate-600/50'
          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
          } border focus:outline-none focus:ring-1 focus:ring-[#0974BA]/50 transition-colors`}
      >
        <span className={`truncate ${!selectedDescriptor ? 'text-slate-400' : ''}`}>
          {selectedDescriptor
            ? `${selectedDescriptor.descriptor_code} - ${(selectedDescriptor.descriptor_name || '').substring(0, 24)}...`
            : 'Descritor (opcional)...'}
        </span>
        <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={`absolute z-50 mt-1 w-80 rounded-lg shadow-xl border ${darkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'}`} style={{ maxHeight: '320px' }}>
          <div className={`p-2 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <input
              ref={inputRef}
              type="text"
              placeholder="Buscar descritor (código, nome, descrição)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-3 py-2 rounded-md text-xs ${darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-200 text-slate-700'} border focus:outline-none focus:ring-1 focus:ring-[#0974BA]/50`}
            />
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '240px' }}>
            <button
              type="button"
              onClick={() => { onChange(null); setIsOpen(false); setSearchTerm(''); }}
              className={`w-full px-3 py-2 text-left text-xs ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-50 text-slate-500'}`}
            >
              — Nenhum —
            </button>
            {filteredDescriptors.length === 0 ? (
              <div className={`p-4 text-center text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Nenhum descritor encontrado
              </div>
            ) : (
              filteredDescriptors.map(desc => (
                <button
                  key={desc.id}
                  type="button"
                  onClick={() => { onChange(desc.id); setIsOpen(false); setSearchTerm(''); }}
                  className={`w-full px-3 py-2.5 text-left transition-colors ${value === desc.id
                    ? (darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]')
                    : (darkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-slate-50 text-slate-700')
                    }`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 ${darkMode ? 'bg-slate-700 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}>
                      {desc.descriptor_code}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">{desc.descriptor_name}</div>
                      {desc.description && (
                        <div className={`text-[10px] mt-0.5 line-clamp-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {desc.description}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// STEPPER VISUAL
// =====================================================================
function Stepper({ currentStep, completed, darkMode, onJump }) {
  return (
    <div className="flex items-center justify-between mb-8 relative">
      <div className={`absolute top-5 left-0 right-0 h-0.5 ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`} />
      {STEPS.map((step, idx) => {
        const isActive = idx === currentStep;
        const isDone = completed[step.id];
        const clickable = idx <= currentStep || isDone;
        return (
          <button
            key={step.id}
            type="button"
            onClick={() => clickable && onJump(idx)}
            disabled={!clickable}
            className={`relative z-10 flex flex-col items-center gap-2 ${clickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${isActive
                ? 'bg-[#0974BA] border-[#0974BA] text-white scale-110 shadow-lg'
                : isDone
                  ? 'bg-[#13983D] border-[#13983D] text-white'
                  : darkMode
                    ? 'bg-slate-800 border-slate-600 text-slate-400'
                    : 'bg-white border-slate-300 text-slate-500'
                }`}
            >
              {isDone && !isActive ? '✓' : idx + 1}
            </div>
            <div className="text-center">
              <div className={`text-xs font-medium ${isActive ? 'text-[#0974BA]' : darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {step.label}
              </div>
              <div className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {step.hint}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// =====================================================================
// PÁGINA PRINCIPAL - WIZARD
// =====================================================================
export default function Gabaritos() {
  const { user } = useAuth();
  const { darkMode } = useTheme();

  // Estado global do wizard
  const [currentStep, setCurrentStep] = useState(0);
  const [examId, setExamId] = useState(null);
  const [examData, setExamData] = useState(null); // dados completos da prova
  const [setupStatus, setSetupStatus] = useState(null);

  // UI
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Dados auxiliares carregados uma vez
  const [allExams, setAllExams] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [allSchools, setAllSchools] = useState([]);
  const [descriptors, setDescriptors] = useState([]);
  const [s3Files, setS3Files] = useState([]);

  // Restaurar estado do localStorage ao montar
  useEffect(() => {
    const saved = loadWizardState();
    if (saved && saved.examId) {
      setExamId(saved.examId);
      setCurrentStep(saved.currentStep || 0);
    }
  }, []);

  // Persistir estado
  useEffect(() => {
    if (examId) {
      saveWizardState({ examId, currentStep });
    }
  }, [examId, currentStep]);

  // Carregar dados auxiliares
  const loadAuxData = useCallback(async () => {
    try {
      const [examsRes, classesRes, schoolsRes, descRes, s3Res] = await Promise.all([
        apiGet('/exams/', { page_size: 100 }),
        fetchAllPaginated('/classes/', { page_size: 200 }),
        fetchAllPaginated('/schools/', { page_size: 200 }),
        apiGet('/descriptors/', { page_size: 500 }),
        apiGet('/exams/list_s3_files/').catch(() => ({ files: [] })),
      ]);
      setAllExams(examsRes.results || []);
      setAllClasses(Array.isArray(classesRes) ? classesRes : (classesRes.results || []));
      setAllSchools(Array.isArray(schoolsRes) ? schoolsRes : (schoolsRes.results || []));
      setDescriptors(descRes.results || []);
      setS3Files(s3Res.files || []);
    } catch (err) {
      console.error('Erro carregando dados auxiliares:', err);
      setError('Falha ao carregar dados. Recarregue a página.');
    }
  }, []);

  useEffect(() => { loadAuxData(); }, [loadAuxData]);

  // Carregar status do setup sempre que o examId mudar
  const refreshStatus = useCallback(async (id) => {
    const target = id || examId;
    if (!target) { setSetupStatus(null); setExamData(null); return; }
    try {
      const [statusRes, examRes] = await Promise.all([
        apiGet(`/exams/${target}/setup_status/`),
        apiGet(`/exams/${target}/`),
      ]);
      setSetupStatus(statusRes);
      setExamData(examRes);
    } catch (err) {
      console.error('Erro carregando setup_status:', err);
    }
  }, [examId]);

  useEffect(() => { refreshStatus(); }, [examId, refreshStatus]);

  // Mapa de etapas concluídas para o Stepper
  const completed = useMemo(() => ({
    exam: !!setupStatus?.steps?.exam?.done,
    classes: !!setupStatus?.steps?.classes?.done,
    answers: !!setupStatus?.steps?.answer_key?.done,
    template: !!setupStatus?.steps?.template?.done,
    review: !!setupStatus?.ready_to_finish,
  }), [setupStatus]);

  const goNext = () => setCurrentStep(s => Math.min(s + 1, STEPS.length - 1));
  const goPrev = () => setCurrentStep(s => Math.max(s - 1, 0));
  const jumpTo = (idx) => setCurrentStep(idx);

  const handleReset = () => {
    if (!window.confirm('Reiniciar o wizard? A prova em andamento não será apagada, apenas saímos do fluxo.')) return;
    clearWizardState();
    setExamId(null);
    setCurrentStep(0);
    setSetupStatus(null);
    setExamData(null);
  };

  const handleFinish = () => {
    setSuccess('Fluxo de cadastro concluído! A prova está pronta para aplicação.');
    clearWizardState();
    setTimeout(() => {
      setExamId(null);
      setCurrentStep(0);
      setSetupStatus(null);
      setExamData(null);
      setSuccess(null);
      loadAuxData();
    }, 2500);
  };

  // Flash de mensagens
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(t);
  }, [error]);
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(t);
  }, [success]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="flex justify-between items-start mb-6">
        <div>
          <h1 className={`text-3xl font-medium ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
            Cadastro de Gabarito
          </h1>
          <p className={`mt-1 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Fluxo guiado: prova → turmas → respostas → template → revisão
          </p>
        </div>
        <div className="flex gap-2">
          {examId && (
            <button
              onClick={handleReset}
              className={`px-3 py-2 rounded-lg text-sm border ${darkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
            >
              Sair do fluxo
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-[#E83A3C]/10 border border-[#E83A3C]/30 text-[#E83A3C] text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-[#13983D]/10 border border-[#13983D]/30 text-[#13983D] text-sm flex justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="font-bold">×</button>
        </div>
      )}

      <div className={`rounded-xl p-6 ${darkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-200'} shadow-sm`}>
        <Stepper
          currentStep={currentStep}
          completed={completed}
          darkMode={darkMode}
          onJump={(idx) => {
            // Permite pular apenas se tem examId ou se vai pra step 0
            if (idx === 0 || examId) jumpTo(idx);
          }}
        />

        {/* CONTEÚDO DE CADA ETAPA */}
        {currentStep === 0 && (
          <StepExam
            darkMode={darkMode}
            allExams={allExams}
            examId={examId}
            setExamId={(id) => { setExamId(id); }}
            onCreated={(newExam) => {
              setAllExams(prev => [newExam, ...prev]);
              setExamId(newExam.id);
            }}
            onNext={goNext}
            setError={setError}
            setLoading={setLoading}
            loading={loading}
          />
        )}

        {currentStep === 1 && (
          <StepClasses
            darkMode={darkMode}
            examId={examId}
            allClasses={allClasses}
            allSchools={allSchools}
            setupStatus={setupStatus}
            onSaved={refreshStatus}
            onNext={goNext}
            onPrev={goPrev}
            setError={setError}
            setSuccess={setSuccess}
          />
        )}

        {currentStep === 2 && (
          <StepAnswers
            darkMode={darkMode}
            examId={examId}
            examData={examData}
            descriptors={descriptors}
            setupStatus={setupStatus}
            onSaved={refreshStatus}
            onNext={goNext}
            onPrev={goPrev}
            setError={setError}
            setSuccess={setSuccess}
          />
        )}

        {currentStep === 3 && (
          <StepTemplate
            darkMode={darkMode}
            examId={examId}
            examData={examData}
            setupStatus={setupStatus}
            s3Files={s3Files}
            onSaved={(data) => { refreshStatus(); setS3Files(prev => [...prev]); }}
            onRefreshS3={async () => {
              try {
                const res = await apiGet('/exams/list_s3_files/');
                setS3Files(res.files || []);
              } catch { /* ignore */ }
            }}
            onNext={goNext}
            onPrev={goPrev}
            setError={setError}
            setSuccess={setSuccess}
          />
        )}

        {currentStep === 4 && (
          <StepReview
            darkMode={darkMode}
            examData={examData}
            setupStatus={setupStatus}
            allClasses={allClasses}
            onPrev={goPrev}
            onFinish={handleFinish}
          />
        )}
      </div>
    </div>
  );
}

// =====================================================================
// ETAPA 1: PROVA
// =====================================================================
function StepExam({ darkMode, allExams, examId, setExamId, onCreated, onNext, setError, setLoading, loading }) {
  const [mode, setMode] = useState('select'); // 'select' | 'create'
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({
    exam_code: '', exam_name: '', subject: '', school_year: '', description: '',
  });

  const filtered = useMemo(() => {
    if (!searchTerm) return allExams;
    const t = searchTerm.toLowerCase();
    return allExams.filter(e =>
      e.exam_code?.toLowerCase().includes(t) ||
      e.exam_name?.toLowerCase().includes(t) ||
      e.subject?.toLowerCase().includes(t)
    );
  }, [allExams, searchTerm]);

  const canAdvance = !!examId;

  const handleCreate = async () => {
    if (!form.exam_code || !form.exam_name || !form.subject || !form.school_year) {
      setError('Preencha código, nome, disciplina e ano escolar.');
      return;
    }
    setLoading(true);
    try {
      const newExam = await apiPost('/exams/', {
        exam_code: form.exam_code.trim(),
        exam_name: form.exam_name.trim(),
        subject: form.subject.trim(),
        school_year: parseInt(form.school_year, 10),
        description: form.description || '',
      });
      onCreated(newExam);
      setMode('select');
    } catch (err) {
      console.error(err);
      setError('Erro ao criar prova. Verifique se o código já existe.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className={`text-xl font-medium mb-1 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
        1. Selecione ou cadastre a Prova
      </h2>
      <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        Toda a cadeia (turmas, respostas, template) fica ligada a esta prova.
      </p>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setMode('select')}
          className={`px-4 py-2 rounded-lg text-sm ${mode === 'select' ? 'bg-[#0974BA] text-white' : darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'}`}
        >
          Usar prova existente
        </button>
        <button
          onClick={() => setMode('create')}
          className={`px-4 py-2 rounded-lg text-sm ${mode === 'create' ? 'bg-[#0974BA] text-white' : darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'}`}
        >
          Criar nova prova
        </button>
      </div>

      {mode === 'select' ? (
        <>
          <input
            type="text"
            placeholder="Buscar por código, nome ou disciplina..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg text-sm mb-3 ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-300 text-slate-700'} border`}
          />
          <div className={`rounded-lg border max-h-80 overflow-y-auto ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            {filtered.length === 0 ? (
              <div className={`p-6 text-center text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Nenhuma prova encontrada. Crie uma nova acima.
              </div>
            ) : (
              filtered.map(exam => (
                <button
                  key={exam.id}
                  onClick={() => setExamId(exam.id)}
                  className={`w-full text-left p-3 border-b transition-colors ${darkMode ? 'border-slate-800 hover:bg-slate-800' : 'border-slate-100 hover:bg-slate-50'} ${examId === exam.id ? (darkMode ? 'bg-[#0974BA]/20' : 'bg-[#0974BA]/10') : ''}`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className={`font-medium text-sm ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                        {exam.exam_name}
                      </div>
                      <div className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {exam.exam_code} • {exam.subject} • {exam.school_year}º ano
                      </div>
                    </div>
                    {examId === exam.id && (
                      <span className="text-[#0974BA] font-bold">✓</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <input
            placeholder="Código da prova *"
            value={form.exam_code}
            onChange={(e) => setForm({ ...form, exam_code: e.target.value })}
            className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-300'} border`}
          />
          <input
            placeholder="Nome da prova *"
            value={form.exam_name}
            onChange={(e) => setForm({ ...form, exam_name: e.target.value })}
            className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-300'} border`}
          />
          <input
            placeholder="Disciplina *"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-300'} border`}
          />
          <input
            type="number"
            placeholder="Ano escolar * (1-9)"
            min={1}
            max={9}
            value={form.school_year}
            onChange={(e) => setForm({ ...form, school_year: e.target.value })}
            className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-300'} border`}
          />
          <textarea
            placeholder="Descrição (opcional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className={`col-span-2 px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-300'} border`}
            rows={2}
          />
          <button
            onClick={handleCreate}
            disabled={loading}
            className="col-span-2 bg-[#0974BA] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#075a91] disabled:opacity-50"
          >
            {loading ? 'Criando...' : 'Criar prova'}
          </button>
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button
          onClick={onNext}
          disabled={!canAdvance}
          className="bg-[#0974BA] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#075a91] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Avançar: Turmas →
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// ETAPA 2: TURMAS
// =====================================================================
function StepClasses({ darkMode, examId, allClasses, allSchools, setupStatus, onSaved, onNext, onPrev, setError, setSuccess }) {
  const [schoolFilter, setSchoolFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [applicationDate, setApplicationDate] = useState('');
  const [saving, setSaving] = useState(false);

  // Pré-seleciona as turmas já vinculadas
  useEffect(() => {
    if (setupStatus?.steps?.classes?.applications?.length) {
      const s = new Set();
      setupStatus.steps.classes.applications.forEach(a => s.add(a.id_class));
      setSelected(s);
      const firstDate = setupStatus.steps.classes.applications[0].application_date;
      if (firstDate) setApplicationDate(firstDate);
    }
  }, [setupStatus]);

  const filtered = useMemo(() => {
    return allClasses.filter(c => {
      if (schoolFilter && String(c.id_school) !== String(schoolFilter)) return false;
      if (yearFilter && String(c.school_year) !== String(yearFilter)) return false;
      return true;
    });
  }, [allClasses, schoolFilter, yearFilter]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (!applicationDate) { setError('Informe a data de aplicação.'); return; }
    if (selected.size === 0) { setError('Selecione ao menos uma turma.'); return; }
    setSaving(true);
    try {
      await apiPost(`/exams/${examId}/bulk_link_classes/`, {
        classes: Array.from(selected).map(id_class => ({
          id_class,
          application_date: applicationDate,
        })),
      });
      await onSaved();
      setSuccess('Turmas vinculadas com sucesso.');
      onNext();
    } catch (err) {
      console.error(err);
      setError('Erro ao vincular turmas.');
    } finally {
      setSaving(false);
    }
  };

  // Ano LETIVO da turma (2026), que é o que o filtro abaixo compara — não a
  // série. O rótulo dizia "2026º ano", como se fosse a série da turma.
  // Mais recente primeiro: é o ano em que se está trabalhando.
  const years = [...new Set(allClasses.map(c => c.school_year).filter(Boolean))]
    .sort((a, b) => Number(b) - Number(a));

  return (
    <div>
      <h2 className={`text-xl font-medium mb-1 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
        2. Vincule as Turmas que farão a prova
      </h2>
      <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        Cada turma selecionada gera uma aplicação da prova com a mesma data.
      </p>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <SearchableSelect
          value={schoolFilter}
          onChange={setSchoolFilter}
          options={allSchools.map(s => ({ value: s.id, label: s.school_name || s.name }))}
          emptyOptionLabel="Todas as escolas"
          darkMode={darkMode}
          className="min-w-[200px]"
        />
        <SearchableSelect
          value={yearFilter}
          onChange={setYearFilter}
          options={years.map(y => ({ value: y, label: `Ano letivo ${y}` }))}
          emptyOptionLabel="Todos os anos letivos"
          darkMode={darkMode}
          className="min-w-[150px]"
        />
        <input
          type="date"
          value={applicationDate}
          onChange={(e) => setApplicationDate(e.target.value)}
          className={`px-3 py-2 rounded-lg text-sm ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-300'} border`}
        />
      </div>

      <div className={`rounded-lg border max-h-80 overflow-y-auto ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
        {filtered.length === 0 ? (
          <div className={`p-6 text-center text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Nenhuma turma encontrada com esses filtros.
          </div>
        ) : (
          filtered.map(c => {
            const school = allSchools.find(s => s.id === c.id_school);
            return (
              <label
                key={c.id}
                className={`flex items-center gap-3 p-3 border-b cursor-pointer ${darkMode ? 'border-slate-800 hover:bg-slate-800' : 'border-slate-100 hover:bg-slate-50'}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <div className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                    {c.class_name} <span className={`text-xs ml-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{c.school_year}º ano {c.shift ? `• ${c.shift}` : ''}</span>
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {school?.school_name || school?.name || `Escola #${c.id_school}`}
                  </div>
                </div>
              </label>
            );
          })
        )}
      </div>

      <div className={`mt-3 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        {selected.size} turma(s) selecionada(s)
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={onPrev} className={`px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-slate-700 text-slate-300' : 'border-slate-300 text-slate-600'}`}>
          ← Voltar
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0974BA] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#075a91] disabled:opacity-50"
        >
          {saving ? 'Salvando...' : 'Salvar e avançar: Respostas →'}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// ETAPA 3: RESPOSTAS DO GABARITO
// =====================================================================
function StepAnswers({ darkMode, examId, examData, descriptors, setupStatus, onSaved, onNext, onPrev, setError, setSuccess }) {
  const [numQuestions, setNumQuestions] = useState(10);
  const [answers, setAnswers] = useState({});
  const [descMap, setDescMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingKey, setLoadingKey] = useState(true);

  useEffect(() => {
    (async () => {
      if (!examId) return;
      setLoadingKey(true);
      try {
        const key = await apiGet(`/exams/${examId}/answer_key/`);
        if (key.answers && key.answers.length > 0) {
          const a = {}, d = {};
          key.answers.forEach(x => {
            a[x.question_number] = x.correct_answer;
            if (x.descriptor_id) d[x.question_number] = x.descriptor_id;
          });
          setAnswers(a);
          setDescMap(d);
          setNumQuestions(Math.max(key.answers.length, examData?.total_questions || 10));
        } else {
          setNumQuestions(examData?.total_questions || 10);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingKey(false);
      }
    })();
  }, [examId, examData]);

  const handleSave = async () => {
    const answered = Object.keys(answers).length;
    if (answered < numQuestions) {
      if (!window.confirm(`Existem ${numQuestions - answered} questão(ões) sem resposta. Salvar mesmo assim?`)) return;
    }
    setSaving(true);
    try {
      const formatted = [];
      for (let i = 1; i <= numQuestions; i++) {
        if (answers[i]) {
          const item = { question_number: i, correct_answer: answers[i] };
          if (descMap[i]) item.descriptor_id = descMap[i];
          formatted.push(item);
        }
      }
      await apiPost(`/exams/${examId}/save_answer_key/`, { answers: formatted });
      await onSaved();
      setSuccess('Gabarito salvo com sucesso.');
      onNext();
    } catch (err) {
      console.error(err);
      setError('Erro ao salvar gabarito.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className={`text-xl font-medium mb-1 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
        3. Cadastre as respostas do Gabarito
      </h2>
      <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        Marque a letra correta de cada questão e, se quiser, associe um descritor.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <label className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Nº de questões:</label>
        <input
          type="number"
          min={1}
          max={100}
          value={numQuestions}
          onChange={(e) => setNumQuestions(Math.max(1, parseInt(e.target.value || '1', 10)))}
          className={`w-20 px-3 py-1.5 rounded-lg text-sm ${darkMode ? 'bg-slate-800 border-slate-700 text-slate-200' : 'bg-white border-slate-300'} border`}
        />
        <div className={`text-xs ml-auto ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Preenchidas: {Object.keys(answers).length}/{numQuestions}
        </div>
      </div>

      {loadingKey ? (
        <div className="py-12 text-center"><LoadingSpinnerAdvanced /></div>
      ) : (
        <div className={`rounded-lg border max-h-96 overflow-y-auto ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <table className="w-full text-sm">
            <thead className={darkMode ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className={`text-left p-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Q</th>
                <th className={`text-left p-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Resposta</th>
                <th className={`text-left p-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Descritor</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: numQuestions }, (_, i) => i + 1).map(num => (
                <tr key={num} className={`border-t ${darkMode ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className={`p-2 font-medium w-12 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{num}</td>
                  <td className="p-2">
                    <div className="flex gap-1">
                      {LETTERS.map(L => (
                        <button
                          key={L}
                          onClick={() => setAnswers(prev => ({ ...prev, [num]: L }))}
                          className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${answers[num] === L
                            ? 'bg-[#13983D] text-white scale-110'
                            : darkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                          {L}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="p-2 w-80">
                    <SearchableDescriptorSelect
                      value={descMap[num] || null}
                      onChange={(id) => setDescMap(prev => ({ ...prev, [num]: id }))}
                      descriptors={descriptors}
                      darkMode={darkMode}
                      questionNum={num}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={onPrev} className={`px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-slate-700 text-slate-300' : 'border-slate-300 text-slate-600'}`}>
          ← Voltar
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0974BA] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#075a91] disabled:opacity-50"
        >
          {saving ? 'Salvando...' : 'Salvar e avançar: Template →'}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// ETAPA 4: TEMPLATE (S3)
// =====================================================================
function StepTemplate({ darkMode, examId, examData, setupStatus, s3Files, onSaved, onRefreshS3, onNext, onPrev, setError, setSuccess }) {
  const [choice, setChoice] = useState('official'); // 'official' | 'link' | 'upload'
  const [selectedS3, setSelectedS3] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const currentTemplate = setupStatus?.steps?.template;
  const officialUrl = currentTemplate?.official_url || '';

  const handleUseOfficial = async () => {
    setSaving(true);
    try {
      await apiPost(`/exams/${examId}/use_official_template/`, {});
      await onSaved();
      setSuccess('Template oficial vinculado.');
      onNext();
    } catch (err) {
      console.error(err);
      setError('Erro ao vincular template oficial.');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkS3 = async () => {
    if (!selectedS3) { setError('Selecione um arquivo S3 existente.'); return; }
    const file = s3Files.find(f => f.key === selectedS3);
    if (!file) return;
    setSaving(true);
    try {
      await apiPost(`/exams/${examId}/link_s3_file/`, {
        file_url: file.url, file_key: file.key,
      });
      await onSaved();
      setSuccess('Arquivo S3 vinculado.');
      onNext();
    } catch (err) {
      console.error(err);
      setError('Erro ao vincular arquivo S3.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await apiUpload(`/exams/${examId}/upload_file/`, fd);
      await onSaved();
      await onRefreshS3();
      setSuccess('Arquivo enviado para S3.');
      onNext();
    } catch (err) {
      console.error(err);
      setError('Erro no upload.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      <h2 className={`text-xl font-medium mb-1 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
        4. Escolha o Template do Gabarito
      </h2>
      <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        A folha de resposta (PDF) que será impressa e preenchida pelos alunos.
      </p>

      {currentTemplate?.done && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${darkMode ? 'bg-[#13983D]/10 border border-[#13983D]/30 text-[#13983D]' : 'bg-[#13983D]/10 border border-[#13983D]/30 text-[#13983D]'}`}>
          Template atual: <a href={currentTemplate.file_url} target="_blank" rel="noopener noreferrer" className="underline break-all">{currentTemplate.file_url}</a>
          {currentTemplate.is_official && <span className="ml-2 px-2 py-0.5 bg-[#13983D] text-white rounded text-xs">OFICIAL</span>}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-4">
        <button
          onClick={() => setChoice('official')}
          className={`p-4 rounded-lg text-left border-2 transition-all ${choice === 'official' ? 'border-[#0974BA]' : darkMode ? 'border-slate-700' : 'border-slate-200'}`}
        >
          <div className={`font-medium text-sm ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>Usar oficial</div>
          <div className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Template padrão do SIDEB (recomendado)</div>
        </button>
        <button
          onClick={() => setChoice('link')}
          className={`p-4 rounded-lg text-left border-2 transition-all ${choice === 'link' ? 'border-[#0974BA]' : darkMode ? 'border-slate-700' : 'border-slate-200'}`}
        >
          <div className={`font-medium text-sm ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>Arquivo S3 existente</div>
          <div className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Vincular PDF já enviado ao bucket</div>
        </button>
        <button
          onClick={() => setChoice('upload')}
          className={`p-4 rounded-lg text-left border-2 transition-all ${choice === 'upload' ? 'border-[#0974BA]' : darkMode ? 'border-slate-700' : 'border-slate-200'}`}
        >
          <div className={`font-medium text-sm ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>Upload novo</div>
          <div className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Enviar PDF específico para esta prova</div>
        </button>
      </div>

      {choice === 'official' && (
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
          <div className={`text-sm mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
            O template oficial está hospedado em:
          </div>
          <a href={officialUrl} target="_blank" rel="noopener noreferrer" className="text-[#0974BA] underline text-xs break-all">
            {officialUrl}
          </a>
          <button
            onClick={handleUseOfficial}
            disabled={saving}
            className="mt-4 bg-[#0974BA] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#075a91] disabled:opacity-50"
          >
            {saving ? 'Vinculando...' : 'Usar template oficial'}
          </button>
        </div>
      )}

      {choice === 'link' && (
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
          <SearchableSelect
            value={selectedS3}
            onChange={setSelectedS3}
            options={s3Files.map(f => ({ value: f.key, label: f.name }))}
            placeholder="Selecione um arquivo..."
            darkMode={darkMode}
            className="w-full"
          />
          <button
            onClick={handleLinkS3}
            disabled={saving || !selectedS3}
            className="mt-4 bg-[#0974BA] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#075a91] disabled:opacity-50"
          >
            {saving ? 'Vinculando...' : 'Vincular arquivo'}
          </button>
        </div>
      )}

      {choice === 'upload' && (
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleUpload}
            disabled={uploading}
            className={`block w-full text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}
          />
          {uploading && <div className="mt-2 text-sm text-[#0974BA]">Enviando...</div>}
        </div>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={onPrev} className={`px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-slate-700 text-slate-300' : 'border-slate-300 text-slate-600'}`}>
          ← Voltar
        </button>
        <button
          onClick={onNext}
          disabled={!currentTemplate?.done}
          className="bg-[#0974BA] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#075a91] disabled:opacity-40"
        >
          Avançar: Revisão →
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// ETAPA 5: REVISÃO
// =====================================================================
function StepReview({ darkMode, examData, setupStatus, allClasses, onPrev, onFinish }) {
  if (!setupStatus) {
    return <div className="py-12 text-center"><LoadingSpinnerAdvanced /></div>;
  }

  const steps = setupStatus.steps;
  const linkedClasses = (steps.classes?.applications || []).map(app => {
    const c = allClasses.find(cls => cls.id === app.id_class);
    return { ...app, name: c?.class_name || `Turma #${app.id_class}` };
  });

  const Item = ({ label, ok, children }) => (
    <div className={`p-4 rounded-lg border ${ok ? 'border-[#13983D]/40 bg-[#13983D]/5' : 'border-[#E83A3C]/40 bg-[#E83A3C]/5'}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${ok ? 'bg-[#13983D] text-white' : 'bg-[#E83A3C] text-white'}`}>
          {ok ? '✓' : '!'}
        </span>
        <span className={`font-medium text-sm ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{label}</span>
      </div>
      <div className={`text-xs ml-8 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>{children}</div>
    </div>
  );

  return (
    <div>
      <h2 className={`text-xl font-medium mb-1 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
        5. Revisão final
      </h2>
      <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        Confira tudo antes de concluir. Você pode voltar em qualquer etapa para editar.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <Item label="Prova" ok={steps.exam?.done}>
          <div><strong>{examData?.exam_name}</strong></div>
          <div>{examData?.exam_code} • {examData?.subject} • {examData?.school_year}º ano</div>
        </Item>
        <Item label={`Turmas (${steps.classes?.count || 0})`} ok={steps.classes?.done}>
          {linkedClasses.length > 0
            ? linkedClasses.map(c => <div key={c.id}>• {c.name} — {c.application_date}</div>)
            : 'Nenhuma turma vinculada.'}
        </Item>
        <Item label="Gabarito" ok={steps.answer_key?.done}>
          {steps.answer_key?.filled}/{steps.answer_key?.total_questions} questões preenchidas
        </Item>
        <Item label="Template" ok={steps.template?.done}>
          {steps.template?.file_url ? (
            <a href={steps.template.file_url} target="_blank" rel="noopener noreferrer" className="underline break-all">
              {steps.template.is_official ? 'Oficial' : 'Customizado'}
            </a>
          ) : 'Não definido'}
        </Item>
      </div>

      {!setupStatus.ready_to_finish && (
        <div className={`mb-4 p-3 rounded-lg text-sm bg-[#E83A3C]/10 border border-[#E83A3C]/30 text-[#E83A3C]`}>
          Ainda falta completar alguma etapa. Volte e preencha os itens marcados em vermelho.
        </div>
      )}

      <div className="flex justify-between mt-6">
        <button onClick={onPrev} className={`px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-slate-700 text-slate-300' : 'border-slate-300 text-slate-600'}`}>
          ← Voltar
        </button>
        <button
          onClick={onFinish}
          disabled={!setupStatus.ready_to_finish}
          className="bg-[#13983D] text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#0f7a31] disabled:opacity-40"
        >
          Concluir cadastro
        </button>
      </div>
    </div>
  );
}

